import { useState } from 'react'
import { Link as RouterLink, Navigate, useNavigate } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { LoadingButton } from '@mui/lab'
import { Box, Link } from '@mui/material'
import { ApiError } from '@/api/http-client'
import { useSignupUser } from '@/api/user-manager'
import { removeNonNumeric, toast } from '@/helpers'
import { routes } from '@/router'
import { sessionButtonSx } from '../session-button'
import {
  RedirectIfAuthenticated,
  SessionField,
  SessionScreen,
} from '../session-screen'
import { flowProgress } from '../profile/profile-steps'
import { splitFullName, useSignupDraft } from './use-signup-draft'
import { EmailFormData, emailFormSchema } from './signup.schema'

function maskedEmailFrom(details: unknown): string | null {
  if (!details || typeof details !== 'object' || !('masked_email' in details)) {
    return null
  }
  const value = details.masked_email
  return typeof value === 'string' && value ? value : null
}

export function SignupEmailPage() {
  const navigate = useNavigate()
  const { draft, setEmail } = useSignupDraft()
  const [taken, setTaken] = useState(false)
  const [cpfMismatch, setCpfMismatch] = useState(false)

  const { handleSubmit, control, watch } = useForm<EmailFormData>({
    resolver: zodResolver(emailFormSchema),
    defaultValues: { email: draft.email },
    mode: 'onChange',
  })

  const email = watch('email')
  const isValid = emailFormSchema.safeParse({ email }).success && !taken

  const { mutateAsync: signup, isPending } = useSignupUser()

  if (!draft.fullName.trim()) {
    return <Navigate to={routes.session.signup.name} replace />
  }

  if (!draft.taxId.trim()) {
    return <Navigate to={routes.session.signup.cpf} replace />
  }

  return (
    <RedirectIfAuthenticated>
      <Box
        component="form"
        onSubmit={handleSubmit(async ({ email: nextEmail }) => {
          const { firstName, lastName } = splitFullName(draft.fullName)
          const normalized = nextEmail.trim().toLowerCase()

          try {
            await signup({
              first_name: firstName,
              last_name: lastName,
              email: normalized,
              tax_id: removeNonNumeric(draft.taxId),
            })
            setEmail(normalized)
            navigate(routes.session.signup.code)
          } catch (error) {
            if (!(error instanceof ApiError)) return

            if (error.code === 'email_already_registered') {
              setTaken(true)
              return
            }

            if (error.code === 'tax_id_already_registered') {
              navigate(routes.session.signup.cpfTaken, {
                state: { maskedEmail: maskedEmailFrom(error.details) },
              })
              return
            }

            if (error.code === 'tax_id_mismatch') {
              setCpfMismatch(true)
              return
            }

            toast.error(error.message)
          }
        })}
        sx={{ display: 'flex', flex: 1 }}
      >
        <SessionScreen
          title="Digite seu e-mail"
          subtitle="Vamos enviar um código de 4 caracteres para confirmar."
          progress={flowProgress('email', null)}
          onBack={() => navigate(routes.session.signup.cpf)}
          footer={
            <LoadingButton
              type="submit"
              size="large"
              fullWidth
              loading={isPending}
              disabled={!isValid}
              sx={sessionButtonSx()}
            >
              Continuar
            </LoadingButton>
          }
        >
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <SessionField
                {...field}
                id="signup-email"
                label="E-mail"
                type="email"
                autoComplete="email"
                placeholder="seu@email.com"
                errorText={
                  taken ? (
                    <>
                      Já existe uma conta com esse e-mail.{' '}
                      <Link
                        component={RouterLink}
                        to={`${routes.session.signin}?email=${encodeURIComponent(field.value.trim().toLowerCase())}`}
                      >
                        Fazer login
                      </Link>
                    </>
                  ) : cpfMismatch ? (
                    <>
                      Esse e-mail já está vinculado a outro CPF.{' '}
                      <Link
                        component={RouterLink}
                        to={routes.session.signup.cpf}
                      >
                        Corrigir CPF
                      </Link>
                    </>
                  ) : field.value.trim() ? (
                    error?.message
                  ) : undefined
                }
                onChange={(event) => {
                  setTaken(false)
                  setCpfMismatch(false)
                  field.onChange(event.target.value.toLowerCase())
                }}
                autoFocus
              />
            )}
          />
        </SessionScreen>
      </Box>
    </RedirectIfAuthenticated>
  )
}
